/**
 * App shell — builds the persistent chrome (sidebar + header + content host)
 * and wires it to the {@link Router}. Replaces the old static `index.html`
 * markup and the show/hide section logic in `main.ts`.
 * @module app/shell
 */
import type { MeResponse } from "../auth.ts";
import { isImpersonating, signOut, stopImpersonating } from "../auth.ts";
import { escapeHtml, getPrefTimeFormat } from "../core/ui.ts";
import { store } from "../state/store.ts";
import { buildRoutes, findItem, type NavSection, visibleNav } from "./nav.ts";
import { navOverrides, resetNavOverrides } from "./page-visibility.ts";
import { Router } from "./router.ts";

/** Build sidebar markup from the (role-filtered) nav tree. */
function navMarkup(sections: NavSection[]): string {
	return sections
		.map(
			(section) => `
      <div class="nav-section">
        <span class="nav-section-label">${escapeHtml(section.label)}</span>
        ${section.items
					.map(
						(it) => `
          <a class="nav-link${it.dev ? " nav-link--dev" : ""}" data-route="${it.id}">
            <span class="nav-icon">${it.icon}</span>
            <span class="nav-label">${escapeHtml(it.label)}</span>
            ${it.alertBadge ? '<span id="alert-nav-badge" class="nav-badge hidden">0</span>' : ""}
          </a>`,
					)
					.join("")}
      </div>`,
		)
		.join("");
}

/**
 * Mount the shell into `#app`, returning the {@link Router}. The caller is
 * responsible for the initial `router.navigate(...)`.
 */
export function mountShell(me: MeResponse): Router {
	const app = document.getElementById("app") as HTMLElement;
	const sections = visibleNav(
		me.role,
		me.permissions ?? [],
		me.capabilities ?? [],
	);
	const userLabel = me.username || me.email || me.id;

	app.innerHTML = `
    <nav id="sidebar-nav">
      <div class="nav-brand">
        <span class="brand-logo" role="img" aria-label="Udexon"></span>
        <button id="nav-toggle" title="Toggle sidebar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
      </div>
      <div class="nav-links">${navMarkup(sections)}</div>
      <div class="nav-footer">
        <div id="connection-status" class="status-disconnected">
          <span class="nav-icon"><svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor"><circle cx="4" cy="4" r="4"/></svg></span>
          <span class="status-label">Disconnected</span>
        </div>
      </div>
    </nav>

    <div id="main-area">
      <header id="header">
        <div class="header-left">
          <h1 id="page-title">Fleet Dashboard</h1>
        </div>
        <div class="header-right">
          <span id="user-info" class="user-info">${escapeHtml(userLabel)}</span>
          <span id="clock"></span>
          <button id="theme-toggle" class="theme-btn" title="Toggle light/dark mode">
            <svg id="theme-icon-dark" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            <svg id="theme-icon-light" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
          </button>
          <button id="btn-sign-out" class="theme-btn" title="Sign out">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        </div>
      </header>
      <div id="page-content"><div id="view-root"></div></div>
    </div>`;
	app.style.display = "";
	const host = app.querySelector("#view-root") as HTMLElement;

	// Show impersonation banner if active
	if (isImpersonating()) {
		document.body.style.paddingTop = "36px";
		document.getElementById("udx-impersonation-banner")?.remove();

		const banner = document.createElement("div");
		banner.id = "udx-impersonation-banner";
		banner.style.cssText =
			"position:fixed;top:0;left:0;right:0;height:36px;z-index:99999;background:#7c3aed;" +
			"color:#fff;padding:0 16px;display:flex;align-items:center;font-weight:600;" +
			"justify-content:space-between;font-size:13px;font-family:inherit;box-shadow:0 2px 4px rgba(0,0,0,0.2);";

		const leftContent = document.createElement("div");
		leftContent.style.cssText = "display:flex;align-items:center;gap:6px;";
		leftContent.innerHTML = `
			<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:#22d3ee">
				<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
				<path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
			</svg>
			<span>Impersonating <strong>${escapeHtml(userLabel)}</strong> — you are acting as this user</span>
		`;

		const btn = document.createElement("button");
		btn.textContent = "Stop Impersonating";
		btn.style.cssText =
			"background:rgba(255,255,255,.2);border:1px solid rgba(255,255,255,.4);" +
			"color:#fff;padding:4px 12px;border-radius:4px;cursor:pointer;" +
			"font-size:12px;white-space:nowrap;font-weight:600;transition:background 0.2s;";
		btn.addEventListener("mouseenter", () => {
			btn.style.background = "rgba(255,255,255,.3)";
		});
		btn.addEventListener("mouseleave", () => {
			btn.style.background = "rgba(255,255,255,.2)";
		});
		btn.addEventListener("click", () => {
			stopImpersonating();
			document.body.style.paddingTop = "";
			banner.remove();
			window.location.reload();
		});

		banner.appendChild(leftContent);
		banner.appendChild(btn);
		document.body.prepend(banner);
	}

	const router = new Router(host, buildRoutes(sections), (routeId) =>
		syncChrome(routeId),
	);

	wireNav(app, router);
	wireThemeToggle();
	wireNavToggle();
	wireSignOut();
	startClock();
	wireAlertBadge();

	// The per-user layout customization. Fetched through the shared cache so the
	// sidebar and anything else asking about page visibility read one answer -
	// see app/page-visibility.
	resetNavOverrides();
	navOverrides()
		.then((overrides) => {
			if (overrides.size > 0) {
				const customizedSections = sections
					.map((sec) => ({
						...sec,
						items: sec.items.filter((item) => overrides.get(item.id) !== false),
					}))
					.filter((sec) => sec.items.length > 0);

				const navLinks = app.querySelector(".nav-links") as HTMLElement | null;
				if (navLinks) {
					navLinks.innerHTML = navMarkup(customizedSections);
					wireNav(app, router);
					router.updateRoutes(buildRoutes(customizedSections));
					// Restore active highlight
					if (router.active) syncChrome(router.active);
				}
			}
		})
		.catch((err) => console.error("Failed to load nav customizations:", err));

	// Allow views to navigate programmatically without holding a router reference.
	document.addEventListener("nd:navigate", (e: Event) => {
		const detail = (e as CustomEvent<{ route: string }>).detail;
		if (detail?.route) router.navigate(detail.route);
	});

	return router;
}

/** Reflect the active route in the sidebar highlight, page title and scroll. */
function syncChrome(routeId: string): void {
	document.querySelectorAll(".nav-link").forEach((l) => {
		l.classList.toggle("active", (l as HTMLElement).dataset.route === routeId);
	});
	const item = findItem(routeId);
	const title = document.getElementById("page-title");
	if (item && title) title.textContent = item.label;
	document
		.getElementById("page-content")
		?.scrollTo({ top: 0, behavior: "smooth" });
}

function wireNav(app: HTMLElement, router: Router): void {
	app.querySelectorAll(".nav-link").forEach((link) => {
		link.addEventListener("click", (e) => {
			e.preventDefault();
			const route = (link as HTMLElement).dataset.route;
			if (route) router.navigate(route);
		});
	});
}

function wireThemeToggle(): void {
	const btn = document.getElementById("theme-toggle");
	const iconDark = document.getElementById("theme-icon-dark");
	const iconLight = document.getElementById("theme-icon-light");
	if (!btn || !iconDark || !iconLight) return;

	if (localStorage.getItem("theme") === "light") {
		document.documentElement.setAttribute("data-theme", "light");
		iconDark.style.display = "none";
		iconLight.style.display = "";
	}
	btn.addEventListener("click", () => {
		const isLight =
			document.documentElement.getAttribute("data-theme") === "light";
		const next = isLight ? "dark" : "light";
		document.documentElement.classList.add("theme-changing");
		document.documentElement.setAttribute("data-theme", next);
		localStorage.setItem("theme", next);
		iconDark.style.display = next === "dark" ? "" : "none";
		iconLight.style.display = next === "light" ? "" : "none";
		setTimeout(
			() => document.documentElement.classList.remove("theme-changing"),
			400,
		);
	});
}

function wireNavToggle(): void {
	const btn = document.getElementById("nav-toggle");
	const nav = document.getElementById("sidebar-nav");
	if (!btn || !nav) return;
	if (localStorage.getItem("nav-collapsed") === "true")
		nav.classList.add("collapsed");
	btn.addEventListener("click", () => {
		const collapsed = nav.classList.toggle("collapsed");
		localStorage.setItem("nav-collapsed", String(collapsed));
	});
}

function wireSignOut(): void {
	document
		.getElementById("btn-sign-out")
		?.addEventListener("click", async () => {
			await signOut();
			window.location.reload();
		});
}

function startClock(): void {
	const clockEl = document.getElementById("clock");
	if (!clockEl) return;
	const update = () => {
		const hour12 = getPrefTimeFormat() === "12h";
		clockEl.textContent = new Date().toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			hour12: hour12,
		});
	};
	update();
	setInterval(update, 1000);
}

/** Keep the Alerts nav badge in sync with the live alert count. */
function wireAlertBadge(): void {
	const update = () => {
		const badge = document.getElementById("alert-nav-badge");
		if (!badge) return;
		const n = store.getAlerts().filter((a) => a.state === "FIRED").length;
		badge.textContent = String(n);
		badge.classList.toggle("hidden", n === 0);
	};
	store.subscribe(update);
	update();
}
